import { exhaustMap, tap } from 'rxjs';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { FuelSpecsStore } from '@navstation-portal/fuel-specs/data-access/store';
import { PortsStore } from '@navstation-portal/ports/data-access/store';
import { AppStore } from '@navstation-portal/reporting/data-access/app';
import { IOffshoreReport, OffshoreReportState } from '@navstation-portal/reporting/data-model/offshore-report';
import { INIT_MODES, StateField, StateFieldType } from '@navstation-portal/reporting/data-model/vessel-report';
import { toastEvents } from '@navstation-portal/shared/data-access/toaster';
import { mapResponse } from '@ngrx/operators';
import { signalStoreFeature, type } from '@ngrx/signals';
import { Dispatcher, Events, injectDispatch, withEventHandlers } from '@ngrx/signals/events';
import { OffshoreReportService } from '../offshore-report-service/offshore-report.service';
import { OffshoreBunkerFormStore, offshoreBunkerFormEvents } from './form/bunker';
import { OffshoreConsumptionsFormStore, offshoreConsumptionsFormEvents } from './form/consumptions';
import { OffshoreOperationalFormStore, offshoreOperationalEvents } from './form/operational';
import { OffshorePositionAndWeatherFormStore, offshorePositionAndWeatherEvents } from './form/position-and-weather';
import { OffshoreStockFormStore, offshoreStockFormEvents } from './form/stock';
import { offshoreReportEvents } from './offshore-report.events';
import { StoreRecord, isStateField } from './offshore-vm-builder-helpers';

interface ReportState {
  operational: StoreRecord | undefined;
  consumptions: StoreRecord | undefined;
  positionAndLogistics: StoreRecord | undefined;
  stock: StoreRecord | undefined;
  bunker: StoreRecord | undefined;
}

export function withOffshoreReportEffects() {
  return signalStoreFeature(
    { state: type<OffshoreReportState>() },
    withEventHandlers(() => {
      const router = inject(Router);
      const events = inject(Events);
      const appStore = inject(AppStore);
      const portsStore = inject(PortsStore);
      const offshoreReportService = inject(OffshoreReportService);
      const offshoreDispatcher = injectDispatch(offshoreReportEvents);
      const toastDispatcher = injectDispatch(toastEvents);
      const dispatcher = inject(Dispatcher);

      const operationalStore = inject(OffshoreOperationalFormStore);
      const consumptionsStore = inject(OffshoreConsumptionsFormStore);
      const positionAndWeatherStore = inject(OffshorePositionAndWeatherFormStore);
      const stockStore = inject(OffshoreStockFormStore);
      const bunkerStore = inject(OffshoreBunkerFormStore);
      inject(FuelSpecsStore);

      return {
        createReport$: events.on(offshoreReportEvents.createReport).pipe(
          exhaustMap(({ payload: options }) => {
            return offshoreReportService.createReport(options.nextReportNumber).pipe(
              mapResponse({
                next: ({ reportId, report }) =>
                  offshoreDispatcher.createReportSuccess({
                    vesselId: options.vesselId,
                    reportId,
                    report,
                  }),
                error: (err: Error) => offshoreDispatcher.createReportFailed(err),
              })
            );
          })
        ),
        createReportSuccess$: events.on(offshoreReportEvents.createReportSuccess).pipe(
          tap(({ payload: { vesselId, reportId, report } }) => {
            dispatcher.dispatch(
              offshoreOperationalEvents.initialiseFormState({
                initialData: report.operational,
                initMode: INIT_MODES.IMPORT,
              })
            );

            router.navigate(['/site', vesselId, 'offshore-reports', reportId], {
              replaceUrl: true,
            });
          })
        ),
        fetchReport$: events.on(offshoreReportEvents.fetchReport).pipe(
          tap(({ payload: reportId }) => {
            const vesselId = appStore.selectedVesselId() || appStore.defaultVesselId();
            if (!vesselId) {
              offshoreDispatcher.fetchReportFailed('Vessel ID is required to fetch report');
              return;
            }

            const ports = portsStore.ports();
            offshoreReportService
              .fetchReport(vesselId, reportId, ports)
              .pipe(
                mapResponse({
                  next: (result) => offshoreDispatcher.fetchReportSuccess(result.report),
                  error: (err: Error) => offshoreDispatcher.fetchReportFailed(err.message),
                })
              )
              .subscribe();
          })
        ),
        fetchReportSuccess$: events.on(offshoreReportEvents.fetchReportSuccess).pipe(
          tap(({ payload: report }) => {
            dispatcher.dispatch(
              offshoreOperationalEvents.initialiseFormState({
                initialData: report.operational,
                initMode: INIT_MODES.IMPORT,
              })
            );
            dispatcher.dispatch(
              offshorePositionAndWeatherEvents.initialiseFormState({
                initialData: report.positionAndLogistics,
                initMode: INIT_MODES.IMPORT,
              })
            );
            dispatcher.dispatch(
              offshoreConsumptionsFormEvents.initialiseFormState({
                initialData: report.consumptions,
                initMode: INIT_MODES.IMPORT,
              })
            );
            dispatcher.dispatch(
              offshoreBunkerFormEvents.initialiseFormState({
                initialData: report.bunker,
                initMode: INIT_MODES.IMPORT,
              })
            );
            dispatcher.dispatch(
              offshoreStockFormEvents.initialiseFormState({
                initialData: report.stock,
                initMode: INIT_MODES.IMPORT,
              })
            );
          })
        ),
        submitReport$: events.on(offshoreReportEvents.submitReport).pipe(
          exhaustMap(({ payload: reportId }) => {
            const report = collateReport({
              operational: {
                reportOptions: operationalStore?.reportOptions?.() ?? {},
                reportDetails: operationalStore?.reportDetails?.() ?? {},
              },
              consumptions: {
                fuelSpecs: consumptionsStore?.fuelSpecs?.(),
                totalConsumptions: consumptionsStore?.totalConsumptions?.(),
              },
              bunker: {
                fuelOilBunkerTable: bunkerStore.fuelOilBunkerTable() ?? {},
              },
              positionAndLogistics: {
                shipPosition: positionAndWeatherStore?.positionAndLogistics?.(),
              },
              stock: {
                fuelRob: stockStore?.fuelRob?.(),
                options: stockStore?.options?.(),
                tankTransfers: stockStore?.tankTransfers?.(),
                fuelWaterRob: stockStore?.fuelWaterRob?.(),
                lubeOilRob: stockStore?.lubeOilRob?.(),
                tanks: stockStore?.tanks?.(),
              },
            });

            return offshoreReportService.submitReport(reportId, report).pipe(
              mapResponse({
                next: (result) => offshoreDispatcher.submitReportSuccess(result),
                error: (err: Error) => offshoreDispatcher.submitReportFailed(err.message),
              })
            );
          })
        ),
        submitReportSuccess$: events.on(offshoreReportEvents.submitReportSuccess).pipe(
          tap(() => {
            toastDispatcher.toastSuccess({
              summary: 'Report Submitted',
              detail: 'Your report has been submitted successfully.',
            });
          })
        ),
        submitReportFailed$: events.on(offshoreReportEvents.submitReportFailed).pipe(
          tap(({ payload: error }) => {
            toastDispatcher.toastError({
              summary: 'Report Submission Failed',
              detail: `Your report could not be submitted: ${error}`,
            });
          })
        ),
        saveReport$: events.on(offshoreReportEvents.saveReport).pipe(
          exhaustMap(({ payload: reportId }) => {
            const report = collateReport({
              operational: {
                reportDetails: operationalStore?.reportDetails?.() ?? {},
                reportOptions: operationalStore?.reportOptions?.() ?? {},
                voyageDetails: operationalStore?.voyageDetails?.() ?? {},
                crewAndPassengers: operationalStore?.crewAndPassengers?.() ?? {},
                miscellaneous: operationalStore?.miscellaneous?.() ?? {},
              },
              consumptions: {
                fuelSpecs: consumptionsStore?.fuelSpecs?.(),
                totalConsumptions: consumptionsStore?.totalConsumptions?.(),
              },
              bunker: {
                fuelOilBunkers: bunkerStore.fuelOilBunkers() ?? {},
                fuelOilBunkerTable: bunkerStore.fuelOilBunkerTable() ?? {},
              },
              positionAndLogistics: {
                shipPosition: positionAndWeatherStore?.positionAndLogistics?.(),
              },
              stock: {
                fuelRob: stockStore?.fuelRob?.(),
                options: stockStore?.options?.(),
                tankTransfers: stockStore?.tankTransfers?.(),
                fuelWaterRob: stockStore?.fuelWaterRob?.(),
                lubeOilRob: stockStore?.lubeOilRob?.(),
                tanks: stockStore?.tanks?.(),
              },
            });

            return offshoreReportService.saveReport(reportId, report).pipe(
              mapResponse({
                next: (result) => offshoreDispatcher.saveReportSuccess({ reportId: reportId, report: result.report }),
                error: (err: Error) => offshoreDispatcher.saveReportFailed(err.message),
              })
            );
          })
        ),
        saveReportSuccess$: events.on(offshoreReportEvents.saveReportSuccess).pipe(
          tap(() => {
            toastDispatcher.toastSuccess({
              summary: 'Report Saved',
              detail: 'Your report has been saved successfully.',
            });
          })
        ),
        deleteReport$: events.on(offshoreReportEvents.deleteReport).pipe(
          exhaustMap(({ payload: reportId }) => {
            return offshoreReportService.deleteReport(reportId).pipe(
              mapResponse({
                next: () => offshoreDispatcher.deleteReportSuccess(reportId),
                error: (err: Error) => offshoreDispatcher.deleteReportFailed(err.message),
              })
            );
          })
        ),
        deleteReportSuccess$: events.on(offshoreReportEvents.deleteReportSuccess).pipe(
          tap(() => {
            const vesselId = appStore.selectedVesselId() || appStore.defaultVesselId();
            router.navigate(['/site', vesselId, 'offshore-reports', 'view-reports']);
          })
        ),
      };
    })
  );
}

const collateReport = ({
  operational,
  positionAndLogistics,
  consumptions,
  bunker,
  stock,
}: ReportState): IOffshoreReport => {
  const report = convertSelectOptionsToCodes({
    operational: operational ? reduceSectionToValues(operational) : {},
    consumptions: consumptions ? reduceSectionToValues(consumptions) : {},
    positionAndLogistics: positionAndLogistics ? reduceSectionToValues(positionAndLogistics) : {},
    bunker: bunker ? reduceSectionToValues(bunker) : {},
    stock: stock ? reduceSectionToValues(stock) : {},
  }) as IOffshoreReport;

  return report as unknown as Partial<IOffshoreReport>;
};

/*
 / Takes a 'form' section and strips out all the pristine, touched, valid data
 / leaving just the values.
*/
const reduceSectionToValues = (sectionIn: StoreRecord) => {
  return Object.keys(sectionIn).reduce(
    (acc, sectionKey) => {
      const section = sectionIn[sectionKey];

      if ('entries' in section && Object.keys(section).length === 1) {
        acc[sectionKey] = unwrapStateValue(section['entries']);
        return acc;
      }

      const sectionValues: Record<string, unknown> = {};

      Object.keys(section).forEach((subSectionKey) => {
        if (subSectionKey === 'entries') {
          acc[sectionKey] = unwrapStateValue(section[subSectionKey]);
          return;
        }

        sectionValues[subSectionKey] = unwrapStateValue(section[subSectionKey]);
      });

      if (acc[sectionKey] !== undefined) {
        return acc;
      }

      acc[sectionKey] = sectionValues;

      return acc;
    },
    {} as Record<string, unknown>
  );
};

const unwrapStateValue = (value: unknown): unknown => {
  if (isStateField(value as Parameters<typeof isStateField>[0])) {
    return (value as StateField<StateFieldType>).value;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => unwrapStateValue(entry));
  }

  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;

    if (Array.isArray(record['entries'])) {
      return record['entries'].map((entry) => unwrapStateValue(entry));
    }

    return Object.keys(record).reduce(
      (acc, key) => {
        acc[key] = unwrapStateValue(record[key]);
        return acc;
      },
      {} as Record<string, unknown>
    );
  }

  return value;
};

const isObjectRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const isSelectOption = (value: unknown): value is { code: string; name: string; disabled?: boolean } => {
  if (!isObjectRecord(value)) {
    return false;
  }

  const keys = Object.keys(value);
  const validKeys = keys.every((key) => key === 'name' || key === 'code' || key === 'disabled' || key === 'categories');

  return validKeys && typeof value['name'] === 'string' && 'code' in value;
};

const convertSelectOptionsToCodes = (value: unknown): unknown => {
  if (value == null) {
    return value;
  }

  if (isSelectOption(value)) {
    return value.code;
  }

  if (value instanceof Date) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map((entry) => convertSelectOptionsToCodes(entry));
  }

  if (!isObjectRecord(value)) {
    return value;
  }

  const transformedEntries = Object.entries(value).map(([key, entry]) => {
    return [key, convertSelectOptionsToCodes(entry)];
  });

  return Object.fromEntries(transformedEntries);
};
